import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState,
  type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes, type KeyboardEvent,
} from 'react'
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react'
import { cn } from './cn'
import { useDraftError, useFieldControl } from './field'
import { INVALID_DRAFT_ATTR, isCommitKey } from './draft'
import { currencySymbol, getFormatDefaults, minorToInput, money, number as fmtNumber, parseMoneyInput } from '@/lib/format'
import { currencyDecimals } from '@shared/money'

/** Shared look of every text-like control (also used by Select/Combobox triggers). */
export const controlClass =
  'group/control relative flex w-full min-w-0 items-center rounded-control border border-input bg-surface text-text shadow-control ' +
  'transition-[border-color,box-shadow] duration-150 ' +
  'hover:border-border-strong dark:hover:border-[#44444f] ' +
  'focus-within:border-focus focus-within:ring-3 focus-within:ring-focus/20 hover:focus-within:border-focus ' +
  'has-[[aria-invalid=true]]:border-danger has-[[aria-invalid=true]]:focus-within:ring-danger/15 ' +
  'has-[input:disabled]:cursor-not-allowed has-[input:disabled]:bg-surface-sunken has-[input:disabled]:text-text-subtle has-[input:disabled]:shadow-none'

const sizes = { sm: 'h-7 text-sm', md: 'h-8 text-base', lg: 'h-10 text-base' } as const
type Size = keyof typeof sizes

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'size'> {
  /** Content before the text (icon, currency symbol, "https://"). */
  prefix?: ReactNode
  /** Content after the text (unit, button, spinner). */
  suffix?: ReactNode
  size?: Size
  /** Marks the control invalid (a surrounding Field with an error does this for you). */
  invalid?: boolean
  /** Class for the outer wrapper (width etc.); `className` targets the <input>. */
  wrapperClassName?: string
}

/**
 * Text input. `<Input value={v} onChange={e => set(e.target.value)} prefix="$" suffix="kg" />`
 * Inside a <Field> it inherits id/aria wiring and the error state.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { prefix, suffix, size = 'md', invalid, className, wrapperClassName, id, disabled, required, ...props }, ref,
) {
  const f = useFieldControl()
  const isInvalid = invalid ?? f?.invalid ?? false
  return (
    <div className={cn(controlClass, sizes[size], wrapperClassName)}>
      {prefix !== undefined && prefix !== null && (
        <span className="pointer-events-none flex h-full shrink-0 items-center pl-3 text-text-subtle [&_svg]:size-4">{prefix}</span>
      )}
      <input
        ref={ref}
        id={id ?? f?.id}
        disabled={disabled ?? f?.disabled}
        required={required ?? f?.required}
        aria-invalid={isInvalid || undefined}
        aria-describedby={props['aria-describedby'] ?? f?.describedBy}
        className={cn(
          'h-full w-full min-w-0 flex-1 bg-transparent px-3 outline-none placeholder:text-text-subtle disabled:cursor-not-allowed',
          prefix !== undefined && prefix !== null && 'pl-2',
          suffix !== undefined && suffix !== null && 'pr-2',
          className,
        )}
        {...props}
      />
      {suffix !== undefined && suffix !== null && (
        <span className="flex h-full shrink-0 items-center gap-1 pr-2 text-text-subtle [&_svg]:size-4">{suffix}</span>
      )}
    </div>
  )
})

export interface SearchInputProps extends Omit<InputProps, 'onChange' | 'value' | 'prefix'> {
  value: string
  onChange: (value: string) => void
  /** Shows a clear (×) button when non-empty. Default true. */
  clearable?: boolean
  /** Marks this as the page's main search: the `/` shortcut focuses it. Default true. */
  pageSearch?: boolean
}

/** Search box with icon, clear button and Esc-to-clear. Debounce in the caller (useDebounce) or use FilterBar. */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { value, onChange, clearable = true, pageSearch = true, placeholder = 'Search', onKeyDown, ...props }, ref,
) {
  const inner = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => inner.current as HTMLInputElement)
  return (
    <Input
      ref={inner}
      type="search"
      role="searchbox"
      value={value}
      placeholder={placeholder}
      autoComplete="off"
      spellCheck={false}
      data-page-search={pageSearch ? '' : undefined}
      prefix={<Search aria-hidden />}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Escape' && value) { e.preventDefault(); e.stopPropagation(); onChange('') }
        onKeyDown?.(e)
      }}
      suffix={clearable && value ? (
        <button type="button" aria-label="Clear search" onClick={() => { onChange(''); inner.current?.focus() }}
          className="grid size-5 place-items-center rounded-full text-text-subtle hover:bg-surface-active hover:text-text [&_svg]:size-3.5">
          <X />
        </button>
      ) : undefined}
      {...props}
    />
  )
})

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean
  /** Grow with content (default true). */
  autosize?: boolean
  minRows?: number
  maxRows?: number
}

/** Multi-line text that grows with its content between minRows and maxRows. */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, autosize = true, minRows = 3, maxRows = 14, className, id, disabled, required, onChange, style, ...props }, ref,
) {
  const f = useFieldControl()
  const inner = useRef<HTMLTextAreaElement>(null)
  useImperativeHandle(ref, () => inner.current as HTMLTextAreaElement)
  const resize = useCallback(() => {
    const el = inner.current
    if (!el || !autosize) return
    const cs = getComputedStyle(el)
    const line = parseFloat(cs.lineHeight) || 20
    const pad = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth)
    el.style.height = 'auto'
    const h = Math.min(Math.max(el.scrollHeight + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth), minRows * line + pad), maxRows * line + pad)
    el.style.height = `${h}px`
    el.style.overflowY = el.scrollHeight + 2 > h ? 'auto' : 'hidden'
  }, [autosize, minRows, maxRows])
  useLayoutEffect(resize, [resize, props.value])
  useEffect(() => {
    if (!autosize || typeof ResizeObserver === 'undefined' || !inner.current) return
    let w = inner.current.clientWidth
    const ro = new ResizeObserver(([e]) => { if (Math.abs(e.contentRect.width - w) > 1) { w = e.contentRect.width; resize() } })
    ro.observe(inner.current)
    return () => ro.disconnect()
  }, [autosize, resize])
  const isInvalid = invalid ?? f?.invalid ?? false
  return (
    <textarea
      ref={inner}
      id={id ?? f?.id}
      rows={minRows}
      disabled={disabled ?? f?.disabled}
      required={required ?? f?.required}
      aria-invalid={isInvalid || undefined}
      aria-describedby={props['aria-describedby'] ?? f?.describedBy}
      onChange={e => { onChange?.(e); if (props.value === undefined) resize() }}
      style={style}
      className={cn(
        'block w-full resize-none rounded-control border border-input bg-surface px-3 py-2 text-base text-text shadow-control outline-none',
        'transition-[border-color,box-shadow] duration-150 placeholder:text-text-subtle hover:border-border-strong',
        'focus:border-focus focus:ring-3 focus:ring-focus/20 aria-[invalid=true]:border-danger',
        'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-subtle',
        !autosize && 'resize-y',
        className,
      )}
      {...props}
    />
  )
})

/** Mirror a draft problem into native constraint validation, so even a plain <form> refuses to submit. */
function useDraftValidity(ref: { current: HTMLInputElement | null }, message: string | null) {
  useEffect(() => { ref.current?.setCustomValidity(message ?? '') }, [ref, message])
}

// ─── NumberInput ────────────────────────────────────────────────────────────────────────

export interface NumberInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type' | 'defaultValue' | 'min' | 'max' | 'step'> {
  value: number | null
  onChange: (value: number | null) => void
  min?: number
  max?: number
  step?: number
  /** Allow decimals (default: integers only). */
  decimals?: number
  /** Show − / + stepper buttons (default true). */
  stepper?: boolean
}

const clamp = (n: number, min?: number, max?: number) => Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n))

function rangeMessage(min?: number, max?: number): string {
  if (min !== undefined && max !== undefined) return `Enter a number from ${fmtNumber(min)} to ${fmtNumber(max)}`
  return min !== undefined ? `Enter ${fmtNumber(min)} or more` : `Enter ${fmtNumber(max!)} or less`
}

/**
 * Numeric input with arrow-key stepping (Shift = ×10), a stepper and range checking. `null` = empty.
 * `<NumberInput value={qty} onChange={setQty} min={0} />`
 * Only valid, in-range numbers are ever emitted. Out-of-range text shows an error and is clamped on
 * blur, Enter or ⌘S (before the form saves); unparsable text blocks saving until fixed.
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  { value, onChange, min, max, step = 1, decimals = 0, stepper = true, onBlur, onFocus, onKeyDown, suffix, disabled, invalid, ...props }, ref,
) {
  const inner = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => inner.current as HTMLInputElement)
  const fmt = (n: number | null) => (n === null || !Number.isFinite(n) ? '' : String(decimals ? Number(n.toFixed(decimals)) : Math.round(n)))
  const [text, setText] = useState(fmt(value))
  const focused = useRef(false)
  useEffect(() => { if (!focused.current) setText(fmt(value)) }, [value]) // eslint-disable-line react-hooks/exhaustive-deps
  const parse = (s: string): number | null => {
    const t = s.trim().replace(',', '.').replace(/^[\u2212\u2013]/, '-')
    if (!t || t === '-') return null
    const re = decimals ? new RegExp(`^-?(?:\\d+(?:\\.\\d{0,${decimals}})?|\\.\\d{1,${decimals}})$`) : /^-?\d+$/
    if (!re.test(t)) return NaN
    const n = Number(t)
    return Number.isFinite(n) && Math.abs(n) <= Number.MAX_SAFE_INTEGER ? n : NaN
  }
  const inRange = (n: number) => (min === undefined || n >= min) && (max === undefined || n <= max)
  const parsed = parse(text)
  const draftError = parsed !== null && Number.isNaN(parsed) ? (decimals ? `Enter a number with up to ${decimals} decimal place${decimals === 1 ? '' : 's'}` : 'Enter a whole number')
    : parsed !== null && !inRange(parsed) ? rangeMessage(min, max) : null
  useDraftError(draftError)
  useDraftValidity(inner, draftError)
  /** Clamp + emit + normalise the text. Unparsable text stays (and keeps blocking saves). */
  const commitText = (t: string) => {
    const n = parse(t)
    if (n !== null && Number.isNaN(n)) return
    const v = n === null ? null : clamp(n, min, max)
    if (v !== value) onChange(v)
    setText(fmt(v))
  }
  const bump = (dir: 1 | -1, big = false) => {
    const base = parsed !== null && !Number.isNaN(parsed) ? clamp(parsed, min, max) : value ?? (min !== undefined && min > 0 ? min - step : 0)
    const raw = base + dir * step * (big ? 10 : 1)
    const next = clamp(decimals ? Number(raw.toFixed(decimals)) : Math.round(raw), min, max)
    onChange(next); setText(fmt(next))
  }
  return (
    <Input
      ref={inner}
      inputMode={decimals ? 'decimal' : 'numeric'}
      value={text}
      disabled={disabled}
      invalid={invalid || !!draftError || undefined}
      {...{ [INVALID_DRAFT_ATTR]: draftError ?? undefined }}
      onFocus={e => { focused.current = true; onFocus?.(e) }}
      onChange={e => {
        setText(e.target.value)
        const n = parse(e.target.value)
        if (n === null) { if (value !== null) onChange(null) }
        else if (!Number.isNaN(n) && inRange(n)) onChange(n)
      }}
      onBlur={e => { focused.current = false; commitText(text); onBlur?.(e) }}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowUp') { e.preventDefault(); bump(1, e.shiftKey) }
        else if (e.key === 'ArrowDown') { e.preventDefault(); bump(-1, e.shiftKey) }
        else if (isCommitKey(e)) {
          commitText(text)
          // Unparsable text can't be committed: Enter must not submit the surrounding form.
          const n = parse(text)
          if (e.key === 'Enter' && n !== null && Number.isNaN(n)) e.preventDefault()
        }
        onKeyDown?.(e)
      }}
      className="tabular"
      suffix={(suffix || stepper) ? (
        <>
          {suffix}
          {stepper && !disabled && (
            <span className="-mr-1 flex h-6 flex-col overflow-hidden rounded-[6px] border border-border">
              <button type="button" tabIndex={-1} aria-label="Increase" onClick={() => bump(1)} disabled={max !== undefined && (value ?? 0) >= max}
                className="grid h-1/2 w-5 place-items-center text-text-subtle hover:bg-surface-active hover:text-text disabled:opacity-40 [&_svg]:size-3!"><ChevronUp /></button>
              <button type="button" tabIndex={-1} aria-label="Decrease" onClick={() => bump(-1)} disabled={min !== undefined && (value ?? 0) <= min}
                className="grid h-1/2 w-5 place-items-center border-t border-border text-text-subtle hover:bg-surface-active hover:text-text disabled:opacity-40 [&_svg]:size-3!"><ChevronDown /></button>
            </span>
          )}
        </>
      ) : undefined}
      {...props}
    />
  )
})

// ─── MoneyInput ─────────────────────────────────────────────────────────────────────────

export interface MoneyInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type' | 'defaultValue' | 'prefix'> {
  /** Amount in MINOR units (cents/paise); null = empty. */
  value: number | null
  /** Receives MINOR units (integer) or null when cleared. Never a float, never an invalid amount. */
  onChange: (minor: number | null) => void
  /** ISO currency; defaults to the current store's. */
  currency?: string
  locale?: string
  allowNegative?: boolean
  /** Minimum/maximum in minor units. */
  min?: number
  max?: number
  /** Show the ISO code after the amount (default true when the symbol is ambiguous, e.g. "$"). */
  showCode?: boolean
}

/**
 * Money field: shows the store currency symbol, accepts locale-formatted typing ("1,999.50",
 * "1.999,50", "Rs. 1,999"), and emits integer minor units. `<MoneyInput value={price} onChange={setPrice} />`
 * Text that isn't a valid amount (typos, over `max`, negative) is never emitted: the field shows
 * why, keeps what was typed, and blocks useForm/StickySaveBar saves until it's fixed.
 */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput(
  { value, onChange, currency, locale, allowNegative = false, min, max, showCode, onBlur, onFocus, onKeyDown, invalid, ...props }, ref,
) {
  const inner = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => inner.current as HTMLInputElement)
  const d = getFormatDefaults()
  const cur = (currency ?? d.currency).toUpperCase()
  const loc = locale ?? d.locale
  const [text, setText] = useState(() => minorToInput(value, cur, loc))
  const [draftError, setDraftError] = useState<string | null>(null)
  const focused = useRef(false)
  useEffect(() => {
    if (!focused.current) { setText(minorToInput(value, cur, loc)); setDraftError(null) }
  }, [value, cur, loc])
  useDraftError(draftError)
  useDraftValidity(inner, draftError)
  const symbol = currencySymbol(cur, loc)
  const ambiguous = showCode ?? (symbol.length === 1 && ['$', '£', '¥', 'kr', '₨'].includes(symbol))
  const decimals = currencyDecimals(cur)
  /** Why `minor` can't be this field's value; null when it can. */
  const problem = (minor: number | null): string | null => {
    if (minor === null) return null
    if (Number.isNaN(minor)) return `Enter an amount like ${minorToInput(199950, cur, loc)}`
    if (!Number.isSafeInteger(minor)) return 'That amount is too large'
    if (!allowNegative && minor < 0) return 'The amount can’t be negative'
    if (min !== undefined && minor < min) return `Enter at least ${money(min, cur, loc)}`
    if (max !== undefined && minor > max) return `Enter at most ${money(max, cur, loc)}`
    return null
  }
  return (
    <Input
      ref={inner}
      inputMode={decimals ? 'decimal' : 'numeric'}
      value={text}
      invalid={invalid || !!draftError || undefined}
      {...{ [INVALID_DRAFT_ATTR]: draftError ?? undefined }}
      prefix={<span className="text-sm font-medium text-text-muted">{symbol}</span>}
      suffix={ambiguous && symbol !== cur ? <span className="text-xs font-medium tracking-wide text-text-subtle">{cur}</span> : undefined}
      onFocus={e => {
        focused.current = true
        // Drop grouping while editing so the caret doesn't jump (only when the text is the value).
        if (!draftError) setText(minorToInput(value, cur, loc, false))
        onFocus?.(e)
        const el = e.target
        requestAnimationFrame(() => { if (document.activeElement === el) el.select() })
      }}
      onChange={e => {
        const t = e.target.value
        setText(t)
        const minor = parseMoneyInput(t, cur, loc)
        const why = problem(minor)
        setDraftError(why)
        if (!why && minor !== value) onChange(minor)
      }}
      onBlur={e => {
        focused.current = false
        // A valid amount is re-shown formatted; an invalid one stays as typed (with its error).
        if (!draftError) setText(minorToInput(value, cur, loc))
        onBlur?.(e)
      }}
      onKeyDown={e => {
        // Enter must not submit a form (any form, not only useForm) with an amount that isn't shown.
        if (e.key === 'Enter' && draftError) e.preventDefault()
        onKeyDown?.(e)
      }}
      className="tabular"
      {...props}
    />
  )
})
